import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import tokenStorage from '../utils/tokenStorage';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [token, setToken] = useState(tokenStorage.get());
  const [loading, setLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Configure axios to include token in all requests
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
          setUserType('instructor');
          setIsVerified(response.data.is_verified !== false);
        } catch {
          try {
            const adminResponse = await axios.get(`${API}/admin/me`);
            setUser(adminResponse.data);
            setUserType('admin');
            setIsVerified(true);
          } catch {
            logout();
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, API]);

  const loginInstructor = useCallback(async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token } = response.data;
      
      setToken(access_token);
      tokenStorage.set(access_token);
      
      const profileResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      setUser(profileResponse.data);
      setUserType('instructor');
      setIsVerified(profileResponse.data.is_verified !== false);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  }, [API]);

  const loginAdmin = useCallback(async (username, password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      const { access_token } = response.data;
      
      setToken(access_token);
      tokenStorage.set(access_token);
      
      const profileResponse = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      setUser(profileResponse.data);
      setUserType('admin');
      setIsVerified(true);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Admin login failed' 
      };
    }
  }, [API]);

  const register = useCallback(async (name, email, password, schoolName, phone) => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        name, email, password, school_name: schoolName, phone,
      });
      const { access_token } = response.data;
      
      setToken(access_token);
      tokenStorage.set(access_token);
      
      const profileResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      setUser(profileResponse.data);
      setUserType('instructor');
      setIsVerified(false); // New registrations need verification
      
      return { success: true, needsVerification: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  }, [API]);

  const verifyEmail = useCallback(async (code) => {
    try {
      const response = await axios.post(`${API}/auth/verify-email`, { code });
      if (response.data.is_verified) {
        setIsVerified(true);
        // Refresh user data
        const profileResponse = await axios.get(`${API}/auth/me`);
        setUser(profileResponse.data);
      }
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Verification failed'
      };
    }
  }, [API]);

  const resendVerification = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/resend-verification`);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to resend code'
      };
    }
  }, [API]);

  const resetPassword = useCallback(async (email, newPassword, confirmPassword) => {
    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        email, new_password: newPassword, confirm_password: confirmPassword,
      });
      return { 
        success: true, 
        message: response.data.message,
        instructorName: response.data.instructor_name,
        emailStatus: response.data.email_status
      };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Password reset failed' 
      };
    }
  }, [API]);

  const logout = useCallback(() => {
    setUser(null);
    setUserType(null);
    setToken(null);
    setIsVerified(true);
    tokenStorage.remove();
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // Idle timeout - auto-logout after 30 minutes of inactivity
  const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
  const idleTimerRef = useRef(null);

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (user) {
      idleTimerRef.current = setTimeout(() => {
        logout();
      }, IDLE_TIMEOUT_MS);
    }
  }, [user, logout, IDLE_TIMEOUT_MS]);

  useEffect(() => {
    if (!user) {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      return;
    }
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
    const handler = () => resetIdleTimer();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [user, resetIdleTimer]);

  const value = {
    user, userType,
    loginInstructor, loginAdmin, register, verifyEmail, resendVerification, resetPassword, logout,
    loading, isVerified,
    isAuthenticated: !!user,
    isAdmin: userType === 'admin',
    isInstructor: userType === 'instructor'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
